import {Injectable} from '@angular/core';
import {BaseDaoService, Enterprise, handleError, User} from '@q9elements/ui-kit/common';

import {ACCOUNT_LINK_PROVIDERS} from '../../profile/models/profile';
import {
  LoginPayload,
  LoginResponse,
  LookupEmailPayload,
  LookupEmailResponse,
  ProviderLoginResponse
} from '../models/login.interface';
import {RegisterPayload, RegisterResponse} from '../models/registration.interface';

@Injectable({providedIn: 'root'})
export class AuthenticationDaoService extends BaseDaoService {
  public resendEmail(userIdOrEmail?: string, method: 'post' | 'put' = 'put') {
    const url = `${this.apiUrl}/account/email`;

    return this.http.request<never>(method, url, {body: {userId: userIdOrEmail}});
  }

  public lookupEmail(payload: LookupEmailPayload) {
    return this.http.post<LookupEmailResponse>(`${this.apiUrl}/auth/email-check`, payload);
  }

  public login(payload: LoginPayload | {code: string; codeVerifier?: string; state?: string}) {
    const url = `${this.apiUrl}/auth`;

    return this.http.post<LoginResponse>(url, payload);
  }

  public logOut() {
    return this.http.delete(`${this.apiUrl}/auth`, {responseType: 'text'}).pipe(handleError());
  }

  public register(payload: RegisterPayload) {
    const url = `${this.apiUrl}/auth`;

    return this.http.put<RegisterResponse>(url, payload);
  }

  public registerWithProvider({
    provider,
    idToken,
    accessToken,
    email
  }: {
    provider: ACCOUNT_LINK_PROVIDERS.GOOGLE | ACCOUNT_LINK_PROVIDERS.MICROSOFT;
    idToken: string;
    accessToken: string;
    email?: string;
  }) {
    const url = `${this.apiUrl}/auth/${provider}`;

    return this.http.put<ProviderLoginResponse>(url, {idToken, accessToken, email});
  }

  public confirmUser(userId: string, user: User) {
    const url = `${this.apiUrl}/users/${userId}/invited`;

    return this.http.post<User>(url, user);
  }

  public sendResetPasswordLink(email: string, setup?: boolean) {
    return this.http.post(`${this.apiUrl}/password`, {email, setup});
  }

  public resetPassword(password: string, token: string) {
    return this.http.put(
      `${this.apiUrl}/password/${token}`,
      {newPass: password},
      {responseType: 'text'}
    );
  }

  public confirmEmailChange(token: string) {
    return this.http.get<{isEmailChanged: boolean}>(`${this.apiUrl}/email/confirm/${token}`);
  }

  public loadEnterprise(params: {itemType: 'space' | 'user'}) {
    return this.http
      .get<{
        isEnterpriseActive: boolean;
        isEnterpriseAdmin: boolean;
        enterprise: Enterprise;
      }>(`${this.apiUrl}/account/enterprise/admins`, {params})
      .pipe(handleError());
  }

  public start2FASetup() {
    return this.http.get<{
      qrCodeUrl: string;
      qrCode?: string;
      secret: string;
      backupCodes?: string[];
    }>(`${this.apiUrl}/2fa/setup`, {withCredentials: true});
  }

  public verify2FASetup(token: string) {
    return this.http.post<{backupCodes: string[]}>(
      `${this.apiUrl}/2fa/verify`,
      {token},
      {withCredentials: true}
    );
  }

  public disable2FA(password: string) {
    return this.http.delete(`${this.apiUrl}/2fa`, {body: {password}});
  }

  public generateBackupCodes(password: string) {
    return this.http.post<{backupCodes: string[]}>(`${this.apiUrl}/2fa/backup-codes`, {password});
  }

  public get2FAStatus() {
    return this.http.get<{enabled: boolean; required?: boolean; policyEnforced?: boolean}>(
      `${this.apiUrl}/2fa/status`
    );
  }

  public proceedSSOWithToken(data: any) {
    const url = `${this.apiUrl}/auth/salesforce/2fa`;

    return this.http.post<LoginResponse>(url, data);
  }
}
